import React, { useContext, useEffect, useState } from 'react';
import {
  Card,
  Box,
  Stack,
  Typography,
  Switch,
  Divider,
  CircularProgress,
} from '@mui/material';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import SubscriptionsRoundedIcon from '@mui/icons-material/SubscriptionsRounded';
import { dataFetcher, endpoints } from 'Api';
import { AppContext } from 'Context';
import { V2SettingsShell } from '../../_shared/V2SettingsShell';

const NotificationToggle = ({ type }) => {
  const { setAppSnackbar, setUserDetails, userDetails } = useContext(AppContext);
  const key = type === 'email' ? 'emailNS' : 'subscriptionNS';
  const apiKey = type === 'email' ? 'email_notification' : 'sub_notification';

  const saved = Boolean(userDetails?.[key]);
  const [isOn, setOn] = useState(saved);
  const [busy, setBusy] = useState(false);

  // Adopt the stored value whenever it changes from elsewhere (e.g. the profile
  // fetch resolving after mount) — but never mid-flight, or it would fight the
  // optimistic state.
  useEffect(() => {
    if (!busy) setOn(saved);
  }, [saved]); // eslint-disable-line react-hooks/exhaustive-deps

  // Direct call rather than a useSWR action hook. The hook's key contained
  // Math.random() plus `userDetails`, so it changed on every render; SWR only
  // invokes onSuccess/onError while `key === keyRef.current`, so the re-render
  // caused by setBusy(true) raced the response and the callbacks were silently
  // dropped — leaving the spinner running forever even though the PATCH landed.
  const handleToggle = async (next) => {
    setOn(next); // optimistic
    setBusy(true);
    try {
      const res = await dataFetcher(endpoints.updateMyProfile, { [apiKey]: next ? '1' : '0' });
      if (res?.success) {
        setUserDetails((prev) => ({ ...prev, [key]: next }));
        setAppSnackbar({ isVisible: true, message: res?.message || 'Notification settings updated' });
      } else {
        setOn(!next);
        setAppSnackbar({ isVisible: true, type: 'error', message: res?.message || 'Oops! Something went wrong' });
      }
    } catch (e) {
      setOn(!next);
      setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
      {busy && <CircularProgress size={14} sx={{ color: 'text.secondary' }} />}
      <Switch
        checked={isOn}
        onChange={(e) => handleToggle(e.target.checked)}
        disabled={busy}
      />
    </Stack>
  );
};

const Row = ({ icon: Icon, title, description, control }) => (
  <Stack
    direction="row"
    spacing={2}
    sx={{ alignItems: 'center', justifyContent: 'space-between', py: 2.5 }}
  >
    <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', minWidth: 0, flex: 1 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: 'action.hover',
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          mt: 0.25,
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
        {description && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
            {description}
          </Typography>
        )}
      </Box>
    </Stack>
    {control}
  </Stack>
);

export const NotificationSettingsV2 = () => {
  const { langText } = useContext(AppContext);
  const notifications = langText?.settings?.notifications;

  return (
    <V2SettingsShell
      title={notifications?.title || 'Notifications'}
      description={notifications?.description || 'Edit Notification settings'}
    >
      <Card sx={{ p: { xs: 1.5, md: 2.5 } }}>
        <Box sx={{ px: { xs: 1, md: 1.5 } }}>
          <Row
            icon={EmailRoundedIcon}
            title={notifications?.email || 'Email notifications'}
            description="Receive product updates and account activity by email."
            control={<NotificationToggle type="email" />}
          />
          <Divider />
          <Row
            icon={SubscriptionsRoundedIcon}
            title={notifications?.subscription || 'Subscription notifications'}
            description="Billing reminders, renewal updates, and plan changes."
            control={<NotificationToggle type="subscription" />}
          />
        </Box>
      </Card>
    </V2SettingsShell>
  );
};
